import React, { Component } from 'react'
import { Layout, Loader, Div, P } from '@driftt/tide-core'
import { Icon } from '@driftt/tide-icons'
import { Table } from '@driftt/tide-table'
import { Select } from '@driftt/tide-selects'
import { propEq } from 'ramda'
import moment from 'moment'
import EmptyStateSVG from 'images/empty-state.svg'
import { SingleDatePicker } from 'react-dates'
import { getBusinessEventLogs } from 'api/imports'

import './style.css'

const momentFormat = 'MMM Do, YYYY  h:mm a'

const severityOptions = [
  { label: 'All', value: 'ALL' },
  { label: 'Info', value: 'INFO' },
  { label: 'Warn', value: 'WARN' },
  { label: 'Error', value: 'ERROR' },
  { label: 'Severe', value: 'SEVERE' }
]

const timeFrameOptions = [
  { label: 'Previous Hour', value: '1' },
  { label: 'Previous 6 Hours', value: '6' },
  { label: 'Previous 12 Hours', value: '12' },
  { label: 'Previous 24 Hours', value: '24' }
]

const SeverityRowItem = ({ level }) => {
  switch (level) {
    case 'WARN':
      return <Icon name="warning-triangle" width={18} height={18}/>
    case 'ERROR':
    case 'SEVERE':
      return <Icon name="cancel-circle" width={18} height={18}/>
    default:
      return null
  }
}

// Nearly all of this component was ported from customer-web
class EventTable extends Component {
  constructor(props) {
    super(props)

    this.state = {
      loaded: false,
      events: [],
      severityFilter: 'ALL',
      timeFrame: '24',
      startTime: { label: this.formatMoment(moment()), value: 'now' },
      startDate: moment().startOf('day'),
    }
  }

  componentDidMount() {
    this.getData()
  }

  formatMoment = moment => moment.format('h:mma')

  fetchIntegrationEvents = async (query) => {
    const eventsResponse = await getBusinessEventLogs(query) 
    const events = eventsResponse.data.data

    this.setState({ 
      events, 
      loaded: true 
    })
  }

  getData = () => {
    const { sourceId } = this.props
    const { timeFrame, startTime, startDate } = this.state

    const baseTime = moment(startDate)
    
    if (startTime.value === 'now') {
      const now = moment()
      baseTime.hours(now.hours())
      baseTime.minutes(now.minutes())
    } else {
      baseTime.hours(startTime.value)
      baseTime.startOf('hour')
    }

    // Capture end of minute for inclusive query.
    baseTime.endOf('minute')

    const endTime = baseTime.unix()
    const start = baseTime.subtract(timeFrame, 'hours').unix()
    const offset = start - endTime

    const query = {
      sourceId,
      unit: 'seconds',
      start: endTime * 1000,
      offset: offset
    }
  
    this.fetchIntegrationEvents(query)
    this.setState({ loaded: false })
  }
  
  createTableColumns = () => ([
    { label: '', width: '50px', className: 'events-table--table-header--sticky' },
    { label: 'TIME', width: '200px', className: 'events-table--table-header--sticky' },
    { label: 'EVENT', width: '70%',  className: 'events-table--table-header--sticky' }
  ])
  
  createTableRow = item => ({
    className: `event-table-row--${item.level.toLowerCase()}`,
    data: [
      { content: <span><SeverityRowItem level={item.level}/></span> },
      { content: <p>{moment(item.timestamp).format(momentFormat)}</p> },
      { content: <p>{item.message}</p> }
    ]
  })

  onSeverityFilterChange = filter => this.setState({ severityFilter: filter ? filter.value : 'ALL' })

  getSeverityOptionByValue = value => severityOptions.find(propEq('value', value))

  getTimeFrameOptionByValue = value => timeFrameOptions.find(propEq('value', value))

  filterEvents = (events = []) => {
    const filteredEvents = this.filterEventsBySeverity(events)
    return filteredEvents
  }

  filterEventsBySeverity = (events = []) => {
    const { severityFilter } = this.state

    return severityFilter === 'ALL'
      ? events
      : events.filter(({ level }) => level === severityFilter)
  }

  onTimeFrameChange = timeFrame => this.setState({ timeFrame: timeFrame ? timeFrame.value : '1' }, this.getData)


  createHourOptions = () => {
    const hourOptions = []
    let hrIterator = moment().startOf('day')
    const iteratorEnd = moment().endOf('day')
    const now = moment()

    while (hrIterator < iteratorEnd){
      hourOptions.push({ value: hrIterator.hour(), label: this.formatMoment(hrIterator) })
      
      const next = moment(hrIterator).add(1, 'hour')
      if (hrIterator < now && now < next) {
        hourOptions.push({ value: 'now', label: this.formatMoment(now) })
      }

      hrIterator = next
    }

    return hourOptions
  }

  onDateChange = startDate => this.setState({ startDate }, this.getData)

  onStartTimeChange = startTime => this.setState({ startTime }, this.getData)

  onPickerFocusChange = () => this.setState(prevState => ({ pickerFocused: !prevState.pickerFocused }))
  
  typeFilter = () => {
    return (
      <Div className="event-log-type-filter">
        <Select
          className="event-log-selector type-select"
          value={this.getSeverityOptionByValue(this.state.severityFilter)}
          options={severityOptions}
          onChange={this.onSeverityFilterChange}
          clearable={false}
        />
      </Div>
    )
  }

  renderFilterArea = () => (
    <Div className="event-log-filter">
      <P className="filter-log-copy">Show</P>
      {this.typeFilter()}
      <P className="filter-log-copy">activity from the</P>
      <Select
        className="event-log-selector timeframe-select"
        value={this.getTimeFrameOptionByValue(this.state.timeFrame)}
        options={timeFrameOptions}
        onChange={this.onTimeFrameChange}
        clearable={false}
      />
      <P className="filter-log-copy">starting on</P>
      <SingleDatePicker
        date={this.state.startDate}
        onDateChange={this.onDateChange}
        focused={this.state.pickerFocused}
        onFocusChange={this.onPickerFocusChange}
        numberOfMonths={1}
        isOutsideRange={date => date.startOf('day') > moment()}
      />
      <P className="filter-log-copy">at</P>
      <Select
        className="event-log-selector hour-select"
        value={this.state.startTime}
        onChange={this.onStartTimeChange}
        options={this.createHourOptions()}
        clearable={false}
      />
    </Div>
  )

  renderLoadedState = () => {
    const events = this.filterEvents(this.state.events)

    if (!events || events.length === 0) {
      return (
        <Div className="event-table-empty-state">
          <img src={EmptyStateSVG} alt="empty state" />
          <P> Hmmm... there’s nothing here. Try expanding your timeframe to see data in this log! </P>
        </Div>
      )
    }

    return (
      <Table
        className="event-table"
        columns={this.createTableColumns()}
        data={events.map(this.createTableRow)}
      />
    )
  }

  render() {
    return (
      <Layout className="event-table-wrapper">
        {this.renderFilterArea()}
        {this.state.loaded
          ? this.renderLoadedState()
          : <Loader />
        }
      </Layout>
    )
  }
}

export default EventTable
